/** Subscribes the dashboard to the WebSocket feed and refreshes its queries as messages arrive. */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getDashboardWebSocket, WebSocketMessage } from '@/services/websocket-service';
import { dashboardQueryKeys } from './dashboard-hooks';
import { taskQueryKeys } from './librarian-tasks-hooks';
import { notificationKeys } from './notifications-hooks';

export interface RealTimeUpdate {
  type: 'statistics' | 'activity' | 'task' | 'notification' | 'book_operation';
  data: any;
  timestamp: string;
  animated?: boolean;
}

export interface ConnectionStatus {
  status: 'connecting' | 'connected' | 'disconnected' | 'error';
  lastUpdate?: Date;
  reconnectAttempts?: number;
}

export interface RealTimeDashboardState {
  connectionStatus: ConnectionStatus;
  recentUpdates: RealTimeUpdate[];
  updateCount: number;
  isAutoRefreshEnabled: boolean;
}

/**
 * Hook for real-time dashboard updates via WebSocket
 */
export function useRealTimeDashboard() {
  const queryClient = useQueryClient();
  const ws = getDashboardWebSocket();
  const [state, setState] = useState<RealTimeDashboardState>({
    connectionStatus: { status: 'disconnected' },
    recentUpdates: [],
    updateCount: 0,
    isAutoRefreshEnabled: true
  });

  const updateCountRef = useRef(0);
  const lastUpdateRef = useRef<Date>();

  /**
   * The 30-second `dashboard_update` push from ws/librarian/dashboard/ carries the
   * library-wide counters and the caller's unread-notification count. Patch the
   * cached overview and the topbar badge in place: no HTTP round trip.
   */
  const handleDashboardUpdate = useCallback((data: any) => {
    if (!data) return;
    const stats = data.statistics ?? {};
    queryClient.setQueryData(dashboardQueryKeys.overview(), (oldData: any) => {
      if (!oldData?.statistics) return oldData;
      const s = oldData.statistics;
      return {
        ...oldData,
        statistics: {
          ...s,
          books: { ...s.books, total: stats.totalBooks ?? s.books.total },
          copies: {
            ...s.copies,
            total: stats.totalCopies ?? s.copies.total,
            available: stats.availableCopies ?? s.copies.available,
          },
          loans: {
            ...s.loans,
            active: stats.activeLoans ?? s.loans.active,
            overdue: stats.overdueLoans ?? s.loans.overdue,
            issued_today: stats.loansToday ?? s.loans.issued_today,
            returned_today: stats.returnsToday ?? s.loans.returned_today,
          },
          tasks: { ...s.tasks, pending: data.tasks?.pendingCount ?? s.tasks.pending },
          notifications: {
            ...s.notifications,
            unread: data.notifications?.unreadCount ?? s.notifications.unread,
          },
        },
        generated_at: data.lastUpdated ?? oldData.generated_at,
      };
    });
    if (typeof data.notifications?.unreadCount === 'number') {
      queryClient.setQueryData([...notificationKeys.unread(), 'topbar'], (old: any) =>
        old ? { ...old, unread_count: data.notifications.unreadCount } : old
      );
    }
  }, [queryClient]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    const update: RealTimeUpdate = {
      type: message.type.replace('_update', '') as any,
      data: message.payload,
      timestamp: message.timestamp,
      animated: true
    };

    setState(prev => ({
      ...prev,
      recentUpdates: [update, ...prev.recentUpdates.slice(0, 9)], // Keep last 10 updates
      updateCount: prev.updateCount + 1,
      connectionStatus: {
        ...prev.connectionStatus,
        lastUpdate: new Date()
      }
    }));

    updateCountRef.current += 1;
    lastUpdateRef.current = new Date();

    // Handle different types of updates
    switch (message.type as string) {
      case 'dashboard_update':
        handleDashboardUpdate((message as any).data ?? message.payload);
        break;
      case 'statistics_update':
        handleStatisticsUpdate(message.payload);
        break;
      case 'activity_update':
        handleActivityUpdate(message.payload);
        break;
      case 'task_update':
        handleTaskUpdate(message.payload);
        break;
      case 'notification_update':
        handleNotificationUpdate(message.payload);
        break;
      case 'book_operation':
        handleBookOperationUpdate(message.payload);
        break;
    }
  }, [queryClient]);

  // Handle connection status changes
  const handleConnectionChange = useCallback((event: any) => {
    setState(prev => ({
      ...prev,
      connectionStatus: {
        status: event.type === 'connected' ? 'connected' : 
               event.type === 'disconnected' ? 'disconnected' :
               event.type === 'error' ? 'error' : 'connecting',
        lastUpdate: prev.connectionStatus.lastUpdate,
        reconnectAttempts: event.reconnectAttempts
      }
    }));

    // Show connection status notifications
    if (event.type === 'connected') {
      toast.success('Connected to real-time updates', {
        duration: 2000,
        id: 'websocket-status'
      });
    } else if (event.type === 'disconnected' && !event.manual) {
      toast.warning('Lost connection to real-time updates', {
        duration: 3000,
        id: 'websocket-status'
      });
    } else if (event.type === 'error') {
      toast.error('Connection error - updates may be delayed', {
        duration: 4000,
        id: 'websocket-status'
      });
    }
  }, []);

  // Statistics update handler
  const handleStatisticsUpdate = useCallback((_data: any) => {
    queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.overview() });
  }, [queryClient]);

  // Activity update handler
  const handleActivityUpdate = useCallback((data: any) => {
    queryClient.setQueryData(dashboardQueryKeys.overview(), (oldData: any) => {
      if (!oldData) return oldData;
      const newActivity = Array.isArray(data) ? data : [data];
      const existingActivity = oldData.recent_activity || [];
      return {
        ...oldData,
        recent_activity: [...newActivity, ...existingActivity].slice(0, 50),
      };
    });

    if (data?.priority === 'high' || data?.type === 'book_operation') {
      toast.info(`New activity: ${data.details?.book_title || 'Library operation'}`, {
        duration: 3000
      });
    }
  }, [queryClient]);

  // Task update handler
  const handleTaskUpdate = useCallback((data: any) => {
    // Task queries are keyed ['librarian-tasks', ...] (librarian-tasks-hooks.ts)
    queryClient.invalidateQueries({ queryKey: taskQueryKeys.all });
    
    // Show notification for task completion
    if (data.completed) {
      toast.success(`Task completed: ${data.title}`, {
        duration: 2000
      });
    }
  }, [queryClient]);

  // Notification update handler
  const handleNotificationUpdate = useCallback((data: any) => {
    // Update notifications cache
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    
    // Show toast for new notifications
    if (data.severity === 'error') {
      toast.error(data.message, { duration: 5000 });
    } else if (data.severity === 'warning') {
      toast.warning(data.message, { duration: 4000 });
    } else if (data.severity === 'info') {
      toast.info(data.message, { duration: 3000 });
    }
  }, [queryClient]);

  // Book operation update handler
  const handleBookOperationUpdate = useCallback((data: any) => {
    // Invalidate book-related queries
    queryClient.invalidateQueries({ queryKey: ['catalog'] });
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
    queryClient.invalidateQueries({ queryKey: ['loans'] });
    queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.overview() });
    
    // Show operation notification
    const operationMessages = {
      book_issued: `Book issued: ${data.bookTitle}`,
      book_returned: `Book returned: ${data.bookTitle}`,
      book_added: `New book added: ${data.bookTitle}`,
      bulk_operation: `Bulk operation completed: ${data.itemsProcessed} items`
    };
    
    const message = operationMessages[data.operationType as keyof typeof operationMessages] || 
                   'Book operation completed';
    
    toast.success(message, { duration: 3000 });
  }, [queryClient]);

  // Connect to WebSocket on mount
  useEffect(() => {
    let unsubscribeMessage: (() => void) | undefined;
    let unsubscribeConnection: (() => void) | undefined;
    let mounted = true;

    const connectWebSocket = async () => {
      try {
        // Check if already connected
        if (ws.isConnected()) {
          // Already connected, just subscribe to events
          unsubscribeMessage = ws.on('message', handleWebSocketMessage);
          unsubscribeConnection = ws.on('connection', handleConnectionChange);
          
          if (mounted) {
            setState(prev => ({
              ...prev,
              connectionStatus: { ...prev.connectionStatus, status: 'connected' }
            }));
          }
          return;
        }

        setState(prev => ({
          ...prev,
          connectionStatus: { ...prev.connectionStatus, status: 'connecting' }
        }));

        await ws.connect();

        if (!mounted) return;

        // Subscribe to WebSocket events
        unsubscribeMessage = ws.on('message', handleWebSocketMessage);
        unsubscribeConnection = ws.on('connection', handleConnectionChange);

      } catch (error) {
        console.error('Failed to connect to WebSocket:', error);
        if (mounted) {
          setState(prev => ({
            ...prev,
            connectionStatus: { ...prev.connectionStatus, status: 'error' }
          }));
        }
      }
    };

    connectWebSocket();

    // Cleanup on unmount
    return () => {
      mounted = false;
      unsubscribeMessage?.();
      unsubscribeConnection?.();
      // Don't disconnect - keep connection alive for other components
      // ws.disconnect();
    };
  }, []); // Empty deps - only connect once

  // Manual refresh function
  const refreshDashboard = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.all });
    toast.success('Dashboard refreshed', { duration: 1500 });
  }, [queryClient]);

  // Toggle auto-refresh
  const toggleAutoRefresh = useCallback((enabled: boolean) => {
    setState(prev => ({
      ...prev,
      isAutoRefreshEnabled: enabled
    }));
  }, []);

  // Clear recent updates
  const clearRecentUpdates = useCallback(() => {
    setState(prev => ({
      ...prev,
      recentUpdates: [],
      updateCount: 0
    }));
    updateCountRef.current = 0;
  }, []);

  // Get connection indicator props
  const getConnectionIndicator = useCallback(() => {
    const { status } = state.connectionStatus;
    
    return {
      status,
      color: status === 'connected' ? 'green' : 
             status === 'connecting' ? 'yellow' : 
             status === 'error' ? 'red' : 'gray',
      text: status === 'connected' ? 'Live' :
            status === 'connecting' ? 'Connecting...' :
            status === 'error' ? 'Error' : 'Offline',
      pulse: status === 'connecting'
    };
  }, [state.connectionStatus]);

  // Force reconnection
  const reconnect = useCallback(async () => {
    try {
      ws.disconnect();
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
      await ws.connect();
      toast.success('Reconnected successfully');
    } catch (error) {
      toast.error('Failed to reconnect');
    }
  }, [ws]);

  return {
    // State
    connectionStatus: state.connectionStatus,
    recentUpdates: state.recentUpdates,
    updateCount: state.updateCount,
    isAutoRefreshEnabled: state.isAutoRefreshEnabled,
    isConnected: ws.isConnected(),
    
    // Actions
    refreshDashboard,
    toggleAutoRefresh,
    clearRecentUpdates,
    reconnect,
    
    // Utilities
    getConnectionIndicator,
    
    // WebSocket instance (for advanced usage)
    webSocket: ws
  };
}